// src/components/Queue/SolicitudModal.js
// ---------------------------------------------------------------------------
// S4a · Los dos modales de la pestaña SOLICITUDES:
//
// SolicitudFormModal — "+ Solicitud". Imagen (obligatoria, sube a Storage bajo
//   `solicitudes/{fichaId}/image`), cliente (selector + alta inline con el
//   ClientModal de siempre), nombre y nota. Lo puede usar cualquiera con
//   canViewDashboard: las rules de `work_queue` permiten `create` con ese
//   permiso, y `update` (mover/convertir) exige canManageQueue.
//
// ConvertirModal — desde la columna Lista, elige la estación destino y dispara
//   la conversión (la lógica vive en WorkQueueView, este modal solo pregunta).
// ---------------------------------------------------------------------------
import React, { useState, useRef } from 'react';
import { collection, doc, setDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, ImagePlus, Plus } from 'lucide-react';
import ClientModal from '../ClientModal';
import { STATIONS, STATION_LABEL, validateSolicitud, solicitudDoc } from '../../lib/workQueue.js';

export const SolicitudFormModal = ({ isOpen, onClose, db, storage, user, clients, onClientCreated }) => {
    const [imagen, setImagen] = useState(null);           // { file, preview }
    const [clientId, setClientId] = useState('');
    const [jobName, setJobName] = useState('');
    const [note, setNote] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [clientModalOpen, setClientModalOpen] = useState(false);
    const fileRef = useRef(null);

    if (!isOpen) return null;

    const reset = () => { setImagen(null); setClientId(''); setJobName(''); setNote(''); setError(''); setSaving(false); };

    const handleFile = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        setImagen({ file, preview: URL.createObjectURL(file) });
    };

    // El ClientModal solo recolecta datos: el alta la hace este componente,
    // igual que en NewJobLiteView.
    const handleSaveClient = async (data) => {
        try {
            const ref2 = await addDoc(collection(db, 'clients'), { ...data, createdAt: serverTimestamp() });
            onClientCreated({ id: ref2.id, ...data });
            setClientId(ref2.id);
            setClientModalOpen(false);
        } catch (e) { setError('No se pudo crear el cliente: ' + e.message); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // La imagen se valida por el archivo elegido; la URL existe recién tras subirla.
        const errores = validateSolicitud({ imageUrl: imagen?.file ? 'pendiente' : '', clientId, jobName });
        if (errores.length) { setError(errores[0]); return; }
        setSaving(true);
        try {
            // El id se genera ANTES para que la imagen viva bajo la ficha:
            // solicitudes/{fichaId}/image (prefijo permitido en storage.rules).
            const fichaRef = doc(collection(db, 'work_queue'));
            const sref = storageRef(storage, `solicitudes/${fichaRef.id}/image`);
            await uploadBytes(sref, imagen.file);
            const imageUrl = await getDownloadURL(sref);

            const cliente = clients.find((c) => c.id === clientId);
            await setDoc(fichaRef, {
                ...solicitudDoc({ imageUrl, clientId, clientName: cliente?.name || '', jobName, note }),
                createdAt: new Date(),
                createdBy: user?.uid || null,
                createdByEmail: user?.email || '',
            });
            reset();
            onClose();
        } catch (err) {
            console.error('Error creando la solicitud:', err);
            setError('No se pudo crear la solicitud: ' + (err.message || err.code || ''));
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[110] bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Nueva solicitud</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" title="Cerrar"><X size={20} /></button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Imagen — obligatoria */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Imagen *</label>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="w-full border-2 border-dashed border-gray-300 rounded-lg p-3 flex items-center justify-center gap-2 text-gray-500 hover:border-blue-400 hover:text-blue-600"
                        >
                            {imagen ? (
                                <img src={imagen.preview} alt="Vista previa" className="h-24 object-contain rounded" />
                            ) : (
                                <><ImagePlus size={18} /> Subir imagen (obligatoria)</>
                            )}
                        </button>
                        <input ref={fileRef} type="file" accept="image/*" className="hidden"
                            onChange={(e) => handleFile(e.target.files?.[0])} />
                    </div>

                    {/* Cliente — selector + alta inline */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Cliente *</label>
                        <div className="flex gap-2">
                            <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                                className="flex-1 p-2.5 border border-gray-300 rounded-lg bg-white">
                                <option value="">— Elegir cliente —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <button type="button" onClick={() => setClientModalOpen(true)}
                                title="Crear cliente nuevo"
                                className="px-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-1">
                                <Plus size={16} /> Nuevo
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre *</label>
                        <input type="text" value={jobName} onChange={(e) => setJobName(e.target.value)}
                            placeholder="Qué pide el cliente"
                            className="w-full p-2.5 border border-gray-300 rounded-lg" />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nota</label>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                            placeholder="Detalles, medidas, fecha tentativa… (opcional)"
                            className="w-full p-2.5 border border-gray-300 rounded-lg" />
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <button type="submit" disabled={saving}
                        className="w-full py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                        {saving ? 'Guardando…' : 'Crear solicitud'}
                    </button>
                </form>
            </div>

            <ClientModal isOpen={clientModalOpen} onClose={() => setClientModalOpen(false)} onSave={handleSaveClient} clientData={null} />
        </div>
    );
};

export const ConvertirModal = ({ solicitud, onClose, onConvert, converting }) => {
    const [station, setStation] = useState('IMPRESION');
    if (!solicitud) return null;
    return (
        <div className="fixed inset-0 z-[110] bg-black bg-opacity-60 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-lg font-bold text-gray-800 mb-1">Convertir en trabajo</h2>
                <p className="text-sm text-gray-600 mb-4 truncate" title={solicitud.jobName}>
                    “{solicitud.jobName}” · {solicitud.clientName}
                </p>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Estación inicial de la ficha</label>
                <select value={station} onChange={(e) => setStation(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white mb-4">
                    {STATIONS.map((st) => <option key={st} value={st}>{STATION_LABEL[st]}</option>)}
                </select>
                <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 py-2 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300">Cancelar</button>
                    <button onClick={() => onConvert(solicitud, station)} disabled={converting}
                        className="flex-1 py-2 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:bg-gray-400">
                        {converting ? 'Creando…' : 'Crear trabajo'}
                    </button>
                </div>
            </div>
        </div>
    );
};
